import { BehaviorSubject, combineLatest, interval, Subscription, Subject } from 'rxjs';
import { takeUntil, startWith, distinctUntilChanged } from 'rxjs/operators';
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { SortType } from '@monsido/angular-shared-components';
import { Reporting } from '@monsido/modules/models/api/reporting';
import { deepEqual } from '@client/app/shared/utils/object-utils';
import { MonTableCollection } from '@monsido/ng2/models/table-collection.interface';
import { MeReportingsRepoService, ReportingsGetParams } from '@client/app/services/api/me-reportings-repo/me-reportings-repo.service';

@Component({
    selector: 'mon-received-reports-table',
    templateUrl: './received-reports-table.component.html',
    styleUrls: ['./received-reports-table.component.scss'],
    standalone: false,
})
export class ReceivedReportsTableComponent implements OnInit, OnDestroy {
    @Input() set search (term: string) {
        this._search = term;
        this.search$.next(term);
    }
    get search (): string {
        return this._search;
    }
    @Input() showPagination: boolean = false;
    @Output() total: EventEmitter<number> = new EventEmitter<number>();
    loading: boolean = false;
    search$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    currentSort$: BehaviorSubject<{ by: string; direction: SortType; }> = new BehaviorSubject<{ by: string; direction: SortType; }>({ by: 'created_at', direction: 'desc' });
    receivedReports: MonTableCollection<Reporting> = [];
    page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
    pageSize$: BehaviorSubject<number> = new BehaviorSubject<number>(5);
    destroy$: Subject<void> = new Subject<void>();
    interval$ = interval(10000).pipe(startWith(0), takeUntil(this.destroy$));
    intervalSub?: Subscription;

    private _search: string = '';

    constructor (
        private meReportingsRepoService: MeReportingsRepoService,
    ) {}

    ngOnInit (): void {
        this.setInitPageSize();
        combineLatest([this.page$, this.pageSize$, this.currentSort$, this.search$])
            .pipe(distinctUntilChanged(deepEqual))
            .subscribe(() => {
                this.getReports();
            });
    }

    ngOnDestroy (): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    setInitPageSize (): void {
        if (this.showPagination) {
            this.pageSize$.next(10);
        }
    }

    getReports (): void {
        const params: ReportingsGetParams = {
            page: this.page$.value,
            page_size: this.pageSize$.value,
            sort_dir: this.currentSort$.value.direction,
            sort_by: this.currentSort$.value.by,
            search: this.search,
        };
        this.loading = true;

        this.intervalSub = this.interval$
            .subscribe({
                next: () => {
                    this.meReportingsRepoService.get(params)
                        .then((reports) => {
                            this.total.emit(reports.total);
                            if (!this.showPagination) {
                                reports.total = reports.length;
                            }
                            this.receivedReports = reports;
                            const isAnyProcessing = reports.some(report => report.state === 'processing');

                            if (!isAnyProcessing) {
                                this.destroy$.next();
                            }
                        })
                        .finally(() => this.loading = false);
                },
                error: () => this.loading = false,
            });
    }

    onSortContent (sortPayload: { by?: string; direction?: SortType; }): void {
        const { direction = 'asc', by = '' } = sortPayload;

        this.currentSort$.next({ by, direction });
    }
}
